import { NgOptimizedImage } from '@angular/common';
import { Component, computed, input, signal } from '@angular/core';
import { ExpireDateComponent } from '@shared/components/challenges/elements/expire-date/expire-date.component';
import { SelectionButtonComponent } from '@shared/components/challenges/elements/selection-button/selection-button.component';
import { UnselectionButtonComponent } from '@shared/components/challenges/elements/unselection-button/unselection-button.component';
import { WinCardComponent } from '@shared/components/challenges/elements/win-card/win-card.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { AmountLeftHint, CampaignPhase } from '@shared/constants';
import {
  ElementDirective,
  TooltipDirective,
  TooltipIfTruncatedDirective,
} from '@shared/directives';
import { StepChallenge } from '@shared/factory';
import { StepChallengeVariant2Config } from '@shared/models';
import { AmountPipe, SafePipe } from '@shared/pipes';

import { StepBaseClass } from '../step.base.class';

import { ProgressSectionComponent } from './progress-section/progress-section.component';

@Component({
  selector: 'b2c-variant2',
  styleUrl: './variant2.component.scss',
  templateUrl: './variant2.component.html',
  imports: [
    SafePipe,
    AmountPipe,
    IconComponent,
    ElementDirective,
    NgOptimizedImage,
    WinCardComponent,
    TooltipDirective,
    ProgressSectionComponent,
    SelectionButtonComponent,
    UnselectionButtonComponent,
    TooltipIfTruncatedDirective,
    ExpireDateComponent,
  ],
})
export class Variant2Component extends StepBaseClass {
  /**
   * Render the card the way a shopper sees it once the challenge is selected —
   * its steps on show — while the challenge is in fact still unselected, and
   * drop the selection button. Set by the onboarding swipe deck, where the
   * deck's own pass/select buttons and the swipe gesture do the selecting.
   */
  previewSelected = input<boolean>(false);

  challenge = input.required<StepChallenge>();
  config = input.required<StepChallengeVariant2Config>();

  index = input<number>(0);
  withShadow = input<boolean>(true);
  isBisCampaign = input<boolean>(false);
  isEventChallenge = input<boolean>(false);
  loadBannerInPriority = input<boolean>(false);
  phase = input<CampaignPhase>(CampaignPhase.CURRENT);

  campaign = computed(() => {
    if (this.phase() === CampaignPhase.FUTURE) {
      return this.dataService.futureBisCampaign();
    } else if (this.phase() === CampaignPhase.COMPLETED) {
      return this.dataService.completedBisCampaignsByKeys()[
        this.challenge().campaignId
      ];
    } else {
      return this.dataService.currentBisCampaignsByKeys()[
        this.challenge().campaignId
      ];
    }
  });

  boxShadow = computed<string>(() => {
    if (this.withShadow()) {
      return this.shouldDisplayHints()
        ? this.config().hints?.challengeBoxShadow || ''
        : this.config().cardStyles.boxShadow;
    }
    return 'none';
  });

  // The subheader's info button only opens productScope.description, so it must
  // not be offered when that field is empty for the active locale: the popin
  // would show a title over a blank body. `scopeInfoButton` is keyed on the
  // subType while the text lives on the product scope, and nothing ties the two
  // together.
  protected readonly hasScopeInfo = computed<boolean>(() => {
    const description =
      this.challenge().productScope.description?.[this.shortLocale];
    return `${description ?? ''}`.trim().length > 0;
  });

  title = computed<string>(() => {
    return this.config().subHeader.useDescription
      ? `${this.challenge().productScope.description[this.shortLocale]}`
      : `${this.challenge().productScope.label[this.shortLocale]}`;
  });
  private readonly isHintEligible = computed(() => {
    const abTestData = this.dataService.abTestData();
    if (
      'amount_left_hint' in abTestData &&
      abTestData.amount_left_hint === AmountLeftHint.LEFT_TO_SPEND_PER_CHALLENGE
    ) {
      return (
        this.dataService.mostActiveChallenges().find((challenge) => {
          return challenge.offerId === this.challenge().offerId;
        }) !== undefined
      );
    }
    return false;
  });

  private readonly isHintDismissed = signal(false);

  shouldDisplayHints = computed(
    () => this.isHintEligible() && !this.isHintDismissed()
  );

  toggleHintsBox(): void {
    this.isHintDismissed.update((value) => !value);
  }
}
